import { useState } from 'react';
import { Link } from 'react-router-dom';
import PopupForm from '../PopupForm';

const modules = [
  { tag: 'PM', title: 'Agentic Product Management', desc: 'Align product goals, scope and release priorities with AI-assisted planning, discovery and decision workflows.', layer: 1, nowPrice: 'INR 2,555', wasPrice: 'INR 4,555', agentWorkflow: 'Requirements, Prioritization, Planner,Governance' },
  { tag: 'DEV', title: 'Agentic Development', desc: 'Accelerate implementation through context-aware coding prompts, guardrails and role-specific engineering workflows.', layer: 2, nowPrice: 'INR 2,555', wasPrice: 'INR 4,555', agentWorkflow: 'Backlog,Estimations,FSD,Unit Testing,Governance' },
  { tag: 'QA', title: 'Agentic QA', desc: 'Turn testing into a disciplined system with structured prompts, scenario generation and validation-first execution.', layer: 3, nowPrice: 'INR 2,555', wasPrice: 'INR 4,555', agentWorkflow: 'QA Planner,QA Scenarios,QA Data,Raise Bug,Governance' },
  { tag: 'DEP', title: 'Agentic Deployment', desc: 'Reduce release friction using automated checks, deployment readiness gates and controlled rollout practices.', layer: 4, nowPrice: 'INR 2,555', wasPrice: 'INR 4,555', agentWorkflow: 'Requirements,Infra Design,Deployment,Monitoring,Governance' },
  { tag: 'SUP', title: 'Agentic Support', desc: 'Improve incident response and customer support with guided triage, knowledge workflows and AI-assisted resolution.', layer: 6, nowPrice: 'INR 2,555', wasPrice: 'INR 4,555', agentWorkflow: 'Ticket Analysis, Triage, Resolution, Governance' },
  { tag: 'FIN', title: 'Agentic FinOps', desc: 'Track and optimize cloud and AI spend using usage intelligence, cost guardrails and proactive recommendations.', layer: 7, nowPrice: 'INR 2,555', wasPrice: 'INR 4,555', agentWorkflow: 'Cost Monitor, Optimizer, Budget Guard,Governance' },
  { tag: 'OBS', title: 'Agentic Observability', desc: 'Continuously observe quality signals, detect anomalies early and trigger actionable feedback loops for teams.', layer: 8, nowPrice: 'INR 2,555', wasPrice: 'INR 4,555', agentWorkflow: 'Signal Collector, Anomaly Detector, Alert Router' },
];

const roleLabelMap = {
  PM: 'Product Manager',
  DEV: 'Developer',
  QA: 'Quality Analyst',
  DEP: 'Deployment Engineer',
  GOV: 'Governance',
  SUP: 'Support',
  FIN: 'FinOps',
  OBS: 'Observability',
};

function RoleIcon({ tag }) {
  if (tag === 'PM') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5 19c0-3.1 3-5 7-5s7 1.9 7 5" />
      </svg>
    );
  }

  if (tag === 'DEV') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M9 7 4 12l5 5" />
        <path d="m15 7 5 5-5 5" />
      </svg>
    );
  }

  if (tag === 'QA') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <circle cx="10" cy="10" r="5.2" />
        <path d="m14.2 14.2 4.8 4.8" />
        <path d="m8 10 1.5 1.5 2.8-2.8" />
      </svg>
    );
  }

  if (tag === 'DEP') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M12 4v12" />
        <path d="m7.5 11.5 4.5 4.5 4.5-4.5" />
        <path d="M5 20h14" />
      </svg>
    );
  }

  if (tag === 'GOV') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M12 3 5.5 6v5c0 4 2.7 7.6 6.5 8.9 3.8-1.3 6.5-4.9 6.5-8.9V6z" />
        <path d="m9 11.5 2 2 4-4" />
      </svg>
    );
  }

  if (tag === 'SUP') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M6.5 14.5a5.5 5.5 0 1 1 5 3.5" />
        <path d="M12 19v.5" />
        <path d="M10 8a2.3 2.3 0 1 1 3.7 1.8c-.8.6-1.2 1-1.2 2" />
      </svg>
    );
  }

  if (tag === 'FIN') {
    return (
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M12 4v16" />
        <path d="M16 7.5c0-1.2-1.8-2.2-4-2.2s-4 1-4 2.2 1.8 2.2 4 2.2 4 1 4 2.2-1.8 2.2-4 2.2-4-1-4-2.2" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
      <path d="M4 12h16" />
      <path d="M12 4v16" />
      <circle cx="12" cy="12" r="7" />
    </svg>
  );
}

function HomeAgenticSection() {
  const [isLeadPopupOpen, setIsLeadPopupOpen] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);

  const handleOpenLeadPopup = (module) => {
    setSelectedPhase(module);
    setIsLeadPopupOpen(true);
  };

  const handleCloseLeadPopup = () => {
    setIsLeadPopupOpen(false);
    setSelectedPhase(null);
  };

  const toSlug = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const sourceKey = selectedPhase
    ? `home_agentic_phase_${selectedPhase.layer}`
    : 'home_agentic_phase_unknown';

  const interestKey = selectedPhase
    ? `agentic_phase_${toSlug(selectedPhase.title)}`
    : 'agentic_phase_general';

  return (
    <>
      <section className="band bg-light" id="agentic" aria-labelledby="agentic-title">
        <div className="wrap">
        <div className="head-block center">
          <p className="eyebrow">New - The Umbrella Brand</p>
          <h2 className="section-title" id="agentic-title" style={{ marginTop: '12px' }}>
            The Agentic <span className="red">Stack</span>
          </h2>
          <p className="section-sub">AI agents across your entire software lifecycle. Eight mentor-led layers  take any one on its own, or own the whole stack.</p>
        </div>

        <div className="stack-analogy">
          <div className="stack-side">
            <span className="stack-tag">Full Stack</span>
            <p>Own every <b>layer of the app</b> frontend to backend.</p>
          </div>
          <div className="stack-arrow" aria-hidden="true"></div>
          <div className="stack-side stack-side-red">
            <span className="stack-tag">The Agentic Stack</span>
            <p>Own every <b>phase of building it</b> an AI agent in every layer of the SDLC.</p>
          </div>
        </div>

        <div className="mod-grid">
          {modules.map((m) => (
            <article className="mod-card" key={m.tag}>
              <div className="mod-body">
                <div className="mod-top">
                 
                  <span className="mod-tag" aria-label={roleLabelMap[m.tag] || m.tag} title={roleLabelMap[m.tag] || m.tag}>
                    <RoleIcon tag={m.tag} />
                  </span>
                   <span className="badge badge-workshop">In Person & Online</span>
                </div>
                <h4>{m.title}</h4>
                <div className="mod-dur">Duration: 6 hrs</div>
              
                <p>{m.desc}</p>
                  <div className="mod-workflow" aria-label="Agent workflow">
                  {(m.agentWorkflow || '')
                    .split(',')
                    .map((agent) => agent.trim())
                    .filter(Boolean)
                    .map((agent, index, allAgents) => (
                      <span key={`${m.tag}-${agent}-${index}`} className="mod-workflow-item">
                        <span className="mod-workflow-agent">{agent}</span>
                        {index < allAgents.length - 1 && <span className="mod-workflow-arrow" aria-hidden="true">&rarr;</span>}
                      </span>
                    ))}
                </div>
                <div className="mod-foot">
                  <div>
                    <div className="now">{m.nowPrice}</div>
                    <div className="was">{m.wasPrice}</div>
                  </div>
                  <button
                    type="button"
                    className="mod-chip mod-chip-btn"
                    onClick={() => handleOpenLeadPopup(m)}
                    aria-label={`Tell me more about ${m.title}`}
                  >
                    Tell Me More
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="pkg">
          <div className="pkg-info">
            <p className="eyebrow" style={{ color: '#FF8C7E' }}>Become an Agentic Stack Engineer</p>
            <h3>The Complete Agentic Stack</h3>
            <p>Take all eight mentor-led layers as one continuous journey from product planning through development, QA, deployment and observability.</p>
            <ul className="pkg-list">
              <li><span className="ck" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M20 6L9 17l-5-5" /></svg></span>8 mentor-led workshop layers</li>
              <li><span className="ck" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M20 6L9 17l-5-5" /></svg></span>48 hours of live, hands-on instruction</li>
              <li><span className="ck" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M20 6L9 17l-5-5" /></svg></span>Every phase of the SDLC, now agentic</li>
              <li><span className="ck" aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false"><path d="M20 6L9 17l-5-5" /></svg></span>Certificate of completion</li>
            </ul>
          </div>
          <div className="pkg-pricing">
            <div className="pkg-tier">
              <span className="pkg-tier-label">Single Workshop</span>
              <span className="pkg-tier-amt">INR 14,555<small>/module</small></span>
            </div>
            <div className="pkg-tier pkg-tier-feat">
              <span className="pkg-flag">Full Package</span>
              <span className="pkg-tier-label">All 8 layers</span>
              <span className="pkg-tier-amt">INR 20,555<small>/module</small></span>
            </div>
            <Link className="btn btn-red" to="/workshop" style={{ width: '100%', justifyContent: 'center' }}>Enroll in the Package</Link>
            <Link className="pkg-syllabus" to="/workshop">Explore Full Syllabus</Link>
          </div>
        </div>
        </div>
      </section>

      <PopupForm
        isOpen={isLeadPopupOpen}
        onClose={handleCloseLeadPopup}
        formType="register_now"
        sourceKey={sourceKey}
        interestKey={interestKey}
      />
    </>
  );
}

export default HomeAgenticSection;
